import { ContainerInputProps } from '../input-props';
import { FormLayout } from '../../../quest/program-wizard/common';
import { useDataPart, useStepContext } from '@/app/core/components/step-wizard';
import { yupResolver } from '@hookform/resolvers';
import { ThresholdsModel, thresholdsValidator } from '@ssar-app/domain';
import { useEffect, useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Input,
  InputGroup,
  InputRightAddon,
  SimpleGrid,
} from '@chakra-ui/react';

export const PinningConfigurationContainer = (props: ContainerInputProps) => {
  const { keys } = props;

  const validator = thresholdsValidator;
  const { isStepActive, onStepDeactivate } = useStepContext();
  const [dataPartState, setDataPartState] = useDataPart<ThresholdsModel>(keys);
  const [thresholdsInfo, setThresholdsInfo] = useState<
    Partial<ThresholdsModel>
  >(dataPartState.data || {});
  const [isValidForm, setIsValidForm] = useState<boolean>(false);

  const formMethods = useForm<ThresholdsModel>({
    defaultValues: thresholdsInfo,
    mode: 'onChange',
    context: {},
    resolver: yupResolver(validator, {
      abortEarly: false,
    }),
  });
  const { errors, control, formState, setValue, trigger } = formMethods;

  onStepDeactivate(() => {
    return new Promise((_resolve, _reject) => {
      trigger(['pinningThreshold', 'validationThreshold']).finally(() => {
        updateState();
      });
      _resolve(true);
    });
  });

  const updateState = useCallback(() => {
    setDataPartState((currentState) => {
      const newState = {
        ...currentState,
        isValid: isValidForm,
        isDirty: formState.isDirty,
        data: {
          ...currentState.data,
          ...thresholdsInfo,
        },
      };
      return newState;
    });
  }, [formState, thresholdsInfo, setDataPartState, isValidForm]);

  useEffect(() => {
    if (thresholdsInfo && validator) {
      validator
        .validate(thresholdsInfo)
        .then(() => {
          setIsValidForm(true);
        })
        .catch((_error) => {
          setIsValidForm(false);
        });
    }
  }, [thresholdsInfo, validator]);

  useEffect(() => {
    updateState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValidForm]);

  useEffect(() => {
    if (thresholdsInfo) {
      setValue('pinningThreshold', thresholdsInfo.pinningThreshold, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('validationThreshold', thresholdsInfo.validationThreshold, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [thresholdsInfo, setValue]);

  useEffect(() => {
    if (thresholdsInfo) {
      updateState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thresholdsInfo]);

  if (!isStepActive) {
    return null;
  }
  return (
    <Flex direction='column' w='full'>
      <form>
        <FormLayout header={'Threshold Details'}>
          <SimpleGrid columns={1} width={'full'}>
            <Box>
              <FormControl
                isInvalid={errors.validationThreshold ? true : false}
                isRequired={true}
              >
                <Grid templateColumns='repeat(5,1fr)'>
                  <GridItem justifyContent='end' display='flex'>
                    <FormLabel>Validation Threshold</FormLabel>
                  </GridItem>
                  <GridItem>
                    <Controller
                      name='validationThreshold'
                      control={control}
                      defaultValue={thresholdsInfo.validationThreshold}
                      render={({ value, onChange }) => (
                        <InputGroup>
                          <Input
                            type='number'
                            onChange={(e) => {
                              onChange(e.target.value);
                              const newModel = {
                                ...thresholdsInfo,
                                validationThreshold: Number(e.target.value),
                              };
                              setValue(
                                'validationThreshold',
                                newModel.validationThreshold,
                                {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                },
                              );
                              setThresholdsInfo(newModel);
                            }}
                            defaultValue={value || null}
                            value={value}
                            spellCheck={false}
                          />
                          <InputRightAddon>%</InputRightAddon>
                        </InputGroup>
                      )}
                    ></Controller>
                    <FormErrorMessage>
                      {errors.validationThreshold?.message?.replaceAll(
                        'validationThreshold',
                        'Validation Threshold',
                      )}
                    </FormErrorMessage>
                  </GridItem>
                </Grid>
              </FormControl>
            </Box>
            <Box mt={6}>
              <FormControl
                isInvalid={errors.pinningThreshold ? true : false}
                isRequired={true}
              >
                <Grid templateColumns='repeat(5,1fr)'>
                  <GridItem justifyContent='end' display='flex'>
                    <FormLabel>Pinning Threshold</FormLabel>
                  </GridItem>
                  <GridItem>
                    <Controller
                      name='pinningThreshold'
                      control={control}
                      defaultValue={thresholdsInfo.pinningThreshold}
                      render={({ value, onChange }) => (
                        <InputGroup>
                          <Input
                            type='number'
                            onChange={(e) => {
                              onChange(e.target.value);
                              const newModel = {
                                ...thresholdsInfo,
                                pinningThreshold: Number(e.target.value),
                              };
                              setValue(
                                'pinningThreshold',
                                newModel.pinningThreshold,
                                {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                },
                              );
                              setThresholdsInfo(newModel);
                            }}
                            defaultValue={value || null}
                            value={value}
                            spellCheck={false}
                          />
                          <InputRightAddon>%</InputRightAddon>
                        </InputGroup>
                      )}
                    ></Controller>
                    <FormErrorMessage>
                      {errors.pinningThreshold?.message?.replaceAll(
                        'pinningThreshold',
                        'Pinning Threshold',
                      )}
                    </FormErrorMessage>
                  </GridItem>
                </Grid>
              </FormControl>
            </Box>
          </SimpleGrid>
        </FormLayout>
      </form>
    </Flex>
  );
};
